import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { formatPrice, formatDate } from "../../lib/api";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import ListingPerformanceHeatmap from "../listing/ListingPerformanceHeatmap";
import { OfferStatusBadge } from "../OfferPanel";
import { TradeInStatusBadge } from "../TradeInPanel";
import ConvertListingDialog from "./ConvertListingDialog";
import { formatAutoReject } from "../../lib/formatAutoReject";
import useUserSocket from "../../lib/useUserSocket";
import AnkaufList, { deriveExpiry } from "./AnkaufList";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Plus, Heart, MessageSquare, Trash2, Edit, User, Phone, Send, Handshake, CheckCircle2, XCircle, TrendingDown, ArrowRight, Repeat, ShoppingBag, Loader2, AlertCircle, Gavel, Sparkles, BarChart3, Truck, ArrowRightLeft, KeyRound, QrCode, Unlock, Zap, Eye, Filter, ChevronLeft, ChevronRight, Archive, CalendarClock } from "lucide-react";
import { useListPrefs } from "../../lib/cockpitCollapse";

/**
 * Aus `DashboardPage.jsx` ausgelagerte Tab-Komponenten (Modularisierung P2, Teil 2):
 * MyListings, MyMessages, MyOffers, MyTradeIns, DealerBuyRequests.
 * Reine Props-Komponenten mit eigenem Datenabruf, ohne Bezug auf DashboardPage-State.
 */

export function MyListings({ userId, onGotoDisposition }) {
    const [items, setItems] = useState([]);
    const [qrOverview, setQrOverview] = useState(null);
    // Jun-2026 (User-Wunsch) — Leasing-/Finanzierungs-Rückläufer-Badges (per VIN).
    const [returnBadges, setReturnBadges] = useState({});
    const [convertTarget, setConvertTarget] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);
    const [ageFilter, setAgeFilter] = useState(null); // null | "fresh" | "ripening" | "stale"
    const [brandFilter, setBrandFilter] = useState(null);
    const [bulkRunning, setBulkRunning] = useState(false);
    // Jun-2026 (User-Wunsch) — Blockgröße 5/10/20 (geräteübergreifend gemerkt) + Paginierung.
    const [listPrefs, updateListPrefs] = useListPrefs("my_listings", { pageSize: 10 });
    const pageSize = listPrefs.pageSize || 10;
    const [page, setPage] = useState(0);
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();

    const load = useCallback(async () => {
        const { data } = await api.get(`/listings?owner_id=${userId}`);
        setItems(data);
    }, [userId]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        api.get("/auth/me").then((r) => setCurrentUser(r.data)).catch(() => {});
    }, []);
    // Feb-2026 (User-Wunsch 24 May Iter 17): QR-Scan-Übersicht laden
    useEffect(() => {
        api.get("/dealer/qr-overview").then((r) => setQrOverview(r.data)).catch(() => {});
    }, []);
    useEffect(() => {
        api.get("/dealer/vehicle-return-badges").then((r) => setReturnBadges(r.data?.badges || {})).catch(() => {});
    }, []);
    // Feb-2026: Deep-Link aus Push-Notification ?convert=<listingId> öffnet
    // den Dialog für genau dieses Inserat sobald die Liste geladen ist.
    useEffect(() => {
        const convertId = searchParams.get("convert");
        if (!convertId || items.length === 0) return;
        const target = items.find((l) => l.id === convertId);
        if (target) {
            setConvertTarget(target);
            // URL-Param entfernen, damit Reload den Dialog nicht erneut öffnet.
            const next = new URLSearchParams(searchParams);
            next.delete("convert");
            setSearchParams(next, { replace: true });
        }
    }, [items, searchParams, setSearchParams]);

    // Feb-2026 — Inventory-Cockpit: Alters-Klassifizierung auf Basis created_at.
    const ageBuckets = useMemo(() => {
        const now = Date.now();
        const day = 86400000;
        const out = { fresh: [], ripening: [], stale: [] };
        for (const l of items) {
            if (l.sold) continue;
            const ts = l.created_at ? new Date(l.created_at).getTime() : now;
            const days = Math.floor((now - ts) / day);
            if (days <= 30) out.fresh.push(l);
            else if (days < 120) out.ripening.push(l);
            else out.stale.push(l);
        }
        return out;
    }, [items]);

    const filteredItems = useMemo(() => {
        let out = items;
        if (ageFilter) {
            const ids = new Set(ageBuckets[ageFilter].map((l) => l.id));
            out = out.filter((l) => ids.has(l.id));
        }
        if (brandFilter) {
            out = out.filter((l) => (l.brand || "").toLowerCase() === brandFilter.toLowerCase());
        }
        return out;
    }, [items, ageFilter, brandFilter, ageBuckets]);

    // Paginierung auf Basis der gewählten Blockgröße; Seite wird geklammert.
    const totalPages = Math.max(1, Math.ceil(filteredItems.length / pageSize));
    const safePage = Math.min(page, totalPages - 1);
    const pagedItems = filteredItems.slice(safePage * pageSize, safePage * pageSize + pageSize);

    // Top-3 Marken nach Anzahl unverkaufter Inserate
    const topBrands = useMemo(() => {
        const counts = {};
        for (const l of items) {
            if (l.sold) continue;
            const b = (l.brand || "").trim();
            if (!b) continue;
            counts[b] = (counts[b] || 0) + 1;
        }
        return Object.entries(counts)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 3)
            .map(([brand, count]) => ({ brand, count }));
    }, [items]);

    // Phase 3.87 — Heatmap-Skala: pro Metrik der Max-Wert über alle eigenen Inserate.
    // Wir mappen jeden Wert auf eine Farb-Stufe 0–4 (vier Stufen + leer).
    const maxes = useMemo(() => ({
        b2c: Math.max(1, ...items.map((l) => l.views_b2c || 0)),
        b2b: Math.max(1, ...items.map((l) => l.views_b2b || 0)),
        favs: Math.max(1, ...items.map((l) => l.favorites_count || 0)),
        refs: Math.max(1, ...items.map((l) => l.referrals_count || 0)),
    }), [items]);

    const remove = async (id) => {
        if (!confirm(t("dashboard.delete_confirm"))) return;
        await api.delete(`/listings/${id}`);
        toast.success(t("dashboard.deleted_toast"));
        load();
    };

    // Feb-2026 — Bulk-Aktion: alle stale Festpreis-Inserate via KI-Empfehlung
    // in Auktionen umwandeln. Nimmt sequentiell die /convert/recommendation
    // pro Listing und ruft danach /convert mit den vorgeschlagenen Werten.
    const smartConvertAllStale = async () => {
        const candidates = ageBuckets.stale.filter((l) => l.sale_type === "fixed");
        if (candidates.length === 0) {
            toast.message("Keine stale Festpreis-Inserate gefunden.");
            return;
        }
        if (!window.confirm(`${candidates.length} Inserat${candidates.length === 1 ? "" : "e"} in Auktionen umstellen?\n\nFür jedes Inserat wird die KI-Empfehlung (Startpreis 70 % des Marktwerts, Auktion 7 Tage) automatisch übernommen.`)) return;
        setBulkRunning(true);
        let ok = 0, skipped = 0, failed = 0;
        for (const l of candidates) {
            try {
                const rec = await api.get(`/listings/${l.id}/convert/recommendation`);
                if (rec.data.recommended_target !== "auction" || !rec.data.suggested?.starting_price) {
                    skipped += 1;
                    continue;
                }
                await api.post(`/listings/${l.id}/convert`, {
                    target: "auction",
                    starting_price: rec.data.suggested.starting_price,
                    buy_now_price: rec.data.suggested.buy_now_price,
                    auction_end: rec.data.suggested.auction_end,
                });
                ok += 1;
            } catch {
                failed += 1;
            }
        }
        setBulkRunning(false);
        toast.success(`Bulk-Convert fertig: ${ok} umgestellt, ${skipped} übersprungen, ${failed} Fehler`);
        load();
    };

    const boost = async (id) => {
        // Phase 3.63 — Top-Inserat per €Punkte-Wallet (Stripe-Variante wurde abgelöst).
        let info;
        try {
            info = (await api.get("/listings/boost/info")).data;
        } catch (e) {
            toast.error("Konnte Tarif nicht laden");
            return;
        }
        const msg = `Top-Inserat buchen?\n\nKosten: ${info.cost_points} €Punkte (≈ ${info.cost_eur} €)\nLaufzeit: ${info.duration_days} Tage\nDein Guthaben: ${info.available_points} €Punkte\n\n${info.can_afford ? "OK = jetzt buchen" : "❌ Nicht genug Guthaben — bitte aufladen."}`;
        if (!info.can_afford) { toast.error("Nicht genug €Punkte-Guthaben"); window.alert(msg); return; }
        if (!window.confirm(msg)) return;
        try {
            await api.post(`/listings/${id}/boost`);
            toast.success(`Top-Inserat aktiv für ${info.duration_days} Tage`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Boost fehlgeschlagen");
        }
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h2 className="font-display text-xl font-bold">{t("dashboard.my_listings", { count: items.length })}</h2>
                <div className="flex items-center gap-2 flex-wrap">
                    <Button onClick={() => navigate("/create-listing")} className="bg-[#111827] hover:bg-black text-white font-bold rounded-sm gap-2" data-testid="new-listing-btn">
                        <Plus className="w-4 h-4" /> {t("dashboard.new_listing")}
                    </Button>
                    {currentUser?.role === "b2b" && (
                        <>
                            <Button
                                onClick={() => navigate("/create-listing?sale_type=auction")}
                                variant="outline"
                                className="border-2 border-[#111827] text-[#111827] hover:bg-[#FFCC00]/20 font-bold rounded-sm gap-2"
                                data-testid="new-auction-listing-btn"
                                title="Neues Inserat direkt als Auktion — Basisdaten erfassen, nur Auktionsdaten kommen hinzu."
                            >
                                <Gavel className="w-4 h-4" /> Fahrzeug zur Auktion
                            </Button>
                            <Button
                                onClick={() => navigate("/create-listing?sale_type=rental")}
                                variant="outline"
                                className="border-2 border-[#0EA5E9] text-[#0EA5E9] hover:bg-[#0EA5E9]/10 font-bold rounded-sm gap-2"
                                data-testid="new-rental-listing-btn"
                                title="Neues Inserat direkt zur Vermietung — Basisdaten erfassen, nur Vermietdaten kommen hinzu."
                            >
                                <KeyRound className="w-4 h-4" /> Fahrzeug zur Vermietung
                            </Button>
                        </>
                    )}
                </div>
            </div>

            {/* Feb-2026 (User-Wunsch 24 May Iter 17): QR-Scan-Tracking-Übersicht.
                Zeigt dem Händler, wie oft Käufer den QR-Code auf seinen
                Inseratsbildern gescannt haben — wertvoller Insight für die
                Off-Platform-Reach. */}
            {qrOverview && qrOverview.total > 0 && (
                <div className="bg-[#111827] text-white rounded-sm p-3 mb-4 flex items-center gap-3 flex-wrap" data-testid="qr-scan-overview">
                    <QrCode className="w-5 h-5 text-[#FFCC00] shrink-0" />
                    <div className="flex flex-col">
                        <div className="text-[10px] uppercase tracking-wider text-[#FFCC00] font-bold">QR-Scans auf Deinen Fotos</div>
                        <div className="font-mono-num text-xl font-black leading-tight" data-testid="qr-scan-total">
                            {qrOverview.total.toLocaleString("de-DE")}
                            <span className="text-xs font-normal text-gray-300 ml-2">gesamt</span>
                        </div>
                    </div>
                    <div className="h-8 w-px bg-gray-700" />
                    <div className="flex flex-col">
                        <div className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Letzte 7 Tage</div>
                        <div className="font-mono-num text-base font-bold leading-tight" data-testid="qr-scan-last7">
                            {qrOverview.last7.toLocaleString("de-DE")} Scans
                        </div>
                    </div>
                    {qrOverview.top_listings?.length > 0 && (
                        <div className="ml-auto text-xs text-gray-300 max-w-md text-right">
                            Top: <span className="font-bold text-white">{qrOverview.top_listings[0].title || qrOverview.top_listings[0].internal_number}</span> ({qrOverview.top_listings[0].qr_scan_count || 0} Scans)
                        </div>
                    )}
                </div>
            )}

            {/* Feb-2026 — Inventory-Cockpit: Lagerage-Klassifizierung */}
            {items.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-sm p-3 mb-4 flex items-center gap-2 flex-wrap" data-testid="inventory-cockpit">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mr-1 inline-flex items-center gap-1">
                        <BarChart3 className="w-3.5 h-3.5" /> Inventar
                    </div>
                    {[
                        { key: "fresh", label: "Frisch", subtitle: "≤ 30 T", bg: "bg-green-50 border-green-300 text-green-800", active: "bg-green-600 text-white border-green-600", count: ageBuckets.fresh.length },
                        { key: "ripening", label: "Reifend", subtitle: "31–119 T", bg: "bg-amber-50 border-amber-300 text-amber-800", active: "bg-amber-600 text-white border-amber-600", count: ageBuckets.ripening.length },
                        { key: "stale", label: "Stale", subtitle: "≥ 120 T", bg: "bg-red-50 border-red-300 text-red-800", active: "bg-red-600 text-white border-red-600", count: ageBuckets.stale.length },
                    ].map((b) => {
                        const isActive = ageFilter === b.key;
                        return (
                            <button
                                key={b.key}
                                type="button"
                                onClick={() => setAgeFilter(isActive ? null : b.key)}
                                className={`flex items-center gap-2 px-3 py-1.5 rounded-sm border font-bold text-xs uppercase tracking-wider transition-colors ${isActive ? b.active : b.bg + " hover:brightness-95"}`}
                                data-testid={`inventory-pill-${b.key}`}
                            >
                                <span className="font-mono-num text-base font-black leading-none">{b.count}</span>
                                <span className="flex flex-col items-start leading-tight">
                                    <span>{b.label}</span>
                                    <span className="text-[9px] opacity-70 normal-case">{b.subtitle}</span>
                                </span>
                            </button>
                        );
                    })}
                    {ageBuckets.stale.filter((l) => l.sale_type === "fixed").length > 0 && (
                        <Button
                            type="button"
                            onClick={smartConvertAllStale}
                            disabled={bulkRunning}
                            size="sm"
                            className="ml-auto bg-[#111827] hover:bg-black text-[#FFCC00] font-bold rounded-sm uppercase tracking-wider text-xs"
                            data-testid="bulk-smart-convert-stale"
                            title="Alle stale Festpreis-Inserate via KI-Empfehlung in Auktionen umstellen"
                        >
                            {bulkRunning ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                            Alle stale → Auktion
                        </Button>
                    )}
                    {/* Top-3 Marken-Pillen */}
                    {topBrands.length > 0 && (
                        <>
                            <div className={`h-6 w-px bg-gray-200 ${ageBuckets.stale.filter((l) => l.sale_type === "fixed").length === 0 ? "ml-auto" : "mx-1"}`} />
                            {topBrands.map(({ brand, count }) => {
                                const isActive = brandFilter === brand;
                                return (
                                    <button
                                        key={brand}
                                        type="button"
                                        onClick={() => setBrandFilter(isActive ? null : brand)}
                                        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-sm border font-bold text-xs uppercase tracking-wider transition-colors ${isActive ? "bg-[#111827] text-[#FFCC00] border-[#111827]" : "bg-white text-gray-700 border-gray-300 hover:border-[#111827] hover:text-[#111827]"}`}
                                        data-testid={`brand-pill-${brand.toLowerCase().replace(/\s+/g, "-")}`}
                                        title={`Nur ${brand} anzeigen`}
                                    >
                                        <span className="font-mono-num font-black">{count}</span>
                                        <span>{brand}</span>
                                    </button>
                                );
                            })}
                        </>
                    )}
                    {(ageFilter || brandFilter) && (
                        <button
                            type="button"
                            onClick={() => { setAgeFilter(null); setBrandFilter(null); }}
                            className="text-[10px] uppercase tracking-wider font-bold text-gray-500 hover:text-[#111827] underline px-2"
                            data-testid="inventory-clear-filter"
                        >
                            Filter zurücksetzen
                        </button>
                    )}
                </div>
            )}

            {filteredItems.length > 0 && (
                <div className="flex items-center justify-between gap-3 flex-wrap mb-3" data-testid="my-listings-controls">
                    {onGotoDisposition && (
                        <button
                            type="button"
                            onClick={onGotoDisposition}
                            className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-[#111827] border border-gray-300 rounded-sm px-3 py-1.5 bg-white hover:border-[#FFCC00] transition-colors"
                            data-testid="my-listings-goto-dispo"
                        >
                            <Filter className="w-3.5 h-3.5" /> Alle Fahrzeuge in Disposition
                        </button>
                    )}
                    <div className="flex items-center gap-3 ml-auto">
                        <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1">
                            Block
                            <select
                                value={pageSize}
                                onChange={(e) => { updateListPrefs({ pageSize: Number(e.target.value) }); setPage(0); }}
                                className="rounded-sm border border-gray-300 bg-white px-2 py-1 text-xs font-bold"
                                data-testid="my-listings-pagesize"
                            >
                                <option value={5}>5er</option>
                                <option value={10}>10er</option>
                                <option value={20}>20er</option>
                            </select>
                        </label>
                        {totalPages > 1 && (
                            <div className="flex items-center gap-1">
                                <button type="button" disabled={safePage === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} className="p-1 rounded-sm border border-gray-300 disabled:opacity-40 hover:border-[#111827]" data-testid="my-listings-prev"><ChevronLeft className="w-4 h-4" /></button>
                                <span className="text-[11px] font-bold text-gray-600 font-mono-num px-1" data-testid="my-listings-page">{safePage + 1} / {totalPages}</span>
                                <button type="button" disabled={safePage >= totalPages - 1} onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} className="p-1 rounded-sm border border-gray-300 disabled:opacity-40 hover:border-[#111827]" data-testid="my-listings-next"><ChevronRight className="w-4 h-4" /></button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {filteredItems.length === 0 ? (
                <div className="bg-white border border-dashed border-gray-300 p-12 text-center rounded-sm">
                    {(ageFilter || brandFilter) ? (
                        <>
                            <p className="text-gray-600 mb-4">Keine Inserate für diese Filter-Kombination.</p>
                            <Button onClick={() => { setAgeFilter(null); setBrandFilter(null); }} variant="outline" className="rounded-sm font-bold">Filter aufheben</Button>
                        </>
                    ) : (
                        <>
                            <p className="text-gray-600 mb-4">{t("dashboard.no_listings")}</p>
                            <Button onClick={() => navigate("/create-listing")} className="bg-[#FFCC00] text-[#111827] hover:bg-[#E6B800] font-bold rounded-sm">
                                {t("dashboard.create_first")}
                            </Button>
                        </>
                    )}
                </div>
            ) : (
                <div className="space-y-3">
                    {pagedItems.map((l) => (
                        <div key={l.id} className={`bg-white border rounded-sm p-4 flex items-center gap-4 group ${l.is_top ? "border-[#DC2626] ring-1 ring-[#DC2626]/20" : "border-gray-200"}`} data-testid="my-listing-item">
                            <Link to={`/listing/${l.id}`} className="w-24 h-24 bg-gray-100 rounded-sm overflow-hidden flex-shrink-0" target="_blank" rel="noopener noreferrer">
                                <img src={l.images?.[0]} alt={l.title} className="w-full h-full object-cover" />
                            </Link>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                    <Link to={`/listing/${l.id}`} className="font-display font-bold text-lg hover:underline line-clamp-1" target="_blank" rel="noopener noreferrer">{l.title}</Link>
                                    {l.is_top && (
                                        <Badge className="bg-[#DC2626] text-white hover:bg-[#DC2626] uppercase tracking-widest text-[10px] font-bold rounded-sm" data-testid={`top-badge-${l.id}`}>
                                            ★ TOP
                                        </Badge>
                                    )}
                                    {/* Jun-2026 (User-Wunsch) — Leasing-/Finanzierungs-Rückläufer-Badge
                                        (verschwindet automatisch 2 Monate nach Vertragsende). */}
                                    {returnBadges[l.id] && (() => {
                                        const b = returnBadges[l.id];
                                        const days = b.end_date ? Math.ceil((new Date(b.end_date) - Date.now()) / 86400000) : null;
                                        const urgent = days !== null && days <= 30;
                                        const soon = days !== null && days <= 90;
                                        return (
                                            <Badge
                                                className={`uppercase tracking-widest text-[10px] font-bold rounded-sm gap-1 ${urgent ? "bg-red-600 hover:bg-red-600 text-white" : soon ? "bg-amber-500 hover:bg-amber-500 text-white" : "bg-violet-600 hover:bg-violet-600 text-white"}`}
                                                title={`${b.kind === "leasing" ? "Leasing" : "Finanzierung"}${b.company ? ` (${b.company})` : ""} — Vertragsende ${b.end_date ? new Date(b.end_date).toLocaleDateString("de-DE") : "unbekannt"}${days !== null ? (days >= 0 ? ` · noch ${days} Tage` : ` · seit ${-days} Tagen abgelaufen`) : ""}`}
                                                data-testid={`return-badge-${l.id}`}
                                            >
                                                <CalendarClock className="w-3 h-3" />
                                                {b.kind === "leasing" ? "Leasing-Rückläufer" : "Finanzierung"}
                                                {b.end_date ? ` · ${new Date(b.end_date).toLocaleDateString("de-DE", { month: "2-digit", year: "2-digit" })}` : ""}
                                            </Badge>
                                        );
                                    })()}
                                </div>
                                <div className="text-sm text-gray-500">EZ {l.year} · {formatDate(l.created_at)}</div>
                                {/* Lead-Quellen Mini-Statistik (Conversion-Insights pro Inserat) */}
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1" data-testid={`listing-lead-stats-${l.id}`}>
                                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-gray-600" title="Aufrufe">
                                        <Eye className="w-3.5 h-3.5 text-gray-400" /> {l.views || 0}
                                    </span>
                                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-rose-600" title="Favoriten / Merkungen">
                                        <Heart className="w-3.5 h-3.5" /> {l.favorites_count || 0}
                                    </span>
                                    <span className={`inline-flex items-center gap-1 text-[11px] font-bold ${l.whatsapp_leads > 0 ? "text-[#128C7E]" : "text-gray-400"}`} title="WhatsApp-Anfragen">
                                        <MessageSquare className="w-3.5 h-3.5" /> {l.whatsapp_leads || 0}
                                    </span>
                                    <span className={`inline-flex items-center gap-1 text-[11px] font-bold ${l.callback_leads > 0 ? "text-blue-600" : "text-gray-400"}`} title="Rückruf-Anfragen / Anrufe">
                                        <Phone className="w-3.5 h-3.5" /> {l.callback_leads || 0}
                                    </span>
                                </div>
                                <div className="font-display font-black text-xl font-mono-num mt-1">{formatPrice(l.price)}</div>
                                {l.is_top && l.top_until && (
                                    <div className="text-[11px] text-[#DC2626] mt-1 font-bold">TOP bis {new Date(l.top_until).toLocaleDateString("de-DE")}</div>
                                )}
                                <ListingPerformanceHeatmap listing={l} maxes={maxes} />
                            </div>
                            <div className="flex flex-col gap-2">
                                {!l.is_top && !l.sold && (
                                    <Button
                                        size="sm"
                                        onClick={() => boost(l.id)}
                                        className="bg-[#DC2626] hover:bg-[#B91C1C] text-white font-bold rounded-sm text-xs uppercase tracking-wider"
                                        data-testid={`boost-btn-${l.id}`}
                                        title="Top-Inserat für 14 Tage (500 €Punkte)"
                                    >
                                        ★ Boost
                                    </Button>
                                )}
                                <Button size="sm" variant="outline" onClick={() => window.open(`/edit-listing/${l.id}`, "_blank", "noopener,noreferrer")} className="rounded-sm" title="Bearbeiten">
                                    <Edit className="w-3.5 h-3.5" />
                                </Button>
                                {/* Jun-2026 (User-Wunsch) — Direktsprung zum Fahrzeug-Archiv (Dokumente). */}
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => window.open(`/edit-listing/${l.id}?focus=archiv`, "_blank", "noopener,noreferrer")}
                                    className="rounded-sm border-gray-300 text-gray-600 hover:border-[#111827] hover:text-[#111827]"
                                    title="Fahrzeug-Archiv öffnen (Dokumente & Unterlagen)"
                                    data-testid={`vehicle-archive-btn-${l.id}`}
                                >
                                    <Archive className="w-3.5 h-3.5" />
                                </Button>
                                {/* Feb-2026 (User-Wunsch 26 May): Disposition-Button — springt direkt
                                    zur Verkaufs-Abwicklungs-Sektion im Edit-Mode (focus=disposition).
                                    Wenn das Inserat bereits verkauft/reserviert ist, wird statt
                                    Disposition der „Fahrzeug freigeben"-Button gerendert. */}
                                {(l.sold || l.status === "sold" || l.status === "reserved") ? (
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={async () => {
                                            if (!window.confirm('Fahrzeug wirklich wieder freigeben?\n\nDer Status springt zurück auf "aktiv", Käufer-Stammdaten werden gelöscht.')) return;
                                            try {
                                                await api.post(`/listings/${l.id}/release`);
                                                toast.success("Fahrzeug freigegeben — Inserat wieder aktiv.");
                                                load();
                                            } catch (e) {
                                                toast.error(e?.response?.data?.detail || "Freigabe fehlgeschlagen.");
                                            }
                                        }}
                                        className="rounded-sm border-amber-600 text-amber-700 hover:bg-amber-50"
                                        title="Fahrzeug freigeben — Status zurück auf aktiv, Käufer-Daten löschen"
                                        data-testid={`release-btn-${l.id}`}
                                    >
                                        <Unlock className="w-3.5 h-3.5" />
                                    </Button>
                                ) : (
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => window.open(`/edit-listing/${l.id}?focus=disposition`, "_blank", "noopener,noreferrer")}
                                        className="rounded-sm border-[#0EA5E9] text-[#0EA5E9] hover:bg-[#0EA5E9]/10"
                                        title="Disposition öffnen (Storno, Kunde, Rechnung, Versand, Übergabe)"
                                        data-testid={`disposition-btn-${l.id}`}
                                    >
                                        <Truck className="w-3.5 h-3.5" />
                                    </Button>
                                )}
                                {!l.sold && (
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => setConvertTarget(l)}
                                        className="rounded-sm border-[#FFCC00] text-[#111827] hover:bg-[#FFCC00]/20"
                                        title="Inserat umstellen (Auktion / Sharing / Festpreis)"
                                        data-testid={`convert-btn-${l.id}`}
                                    >
                                        <ArrowRightLeft className="w-3.5 h-3.5" />
                                    </Button>
                                )}
                                <Button size="sm" variant="outline" onClick={() => remove(l.id)} className="rounded-sm text-red-600 hover:bg-red-50" data-testid="delete-listing">
                                    <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            {convertTarget && (
                <ConvertListingDialog
                    listing={convertTarget}
                    currentUser={currentUser}
                    onClose={() => setConvertTarget(null)}
                    onSuccess={() => load()}
                />
            )}
        </div>
    );
}

// Phase 3.97 — Entwürfe-Liste mit Backend-Sync + localStorage-Fallback
// Lädt Drafts primär aus der DB (geräteübergreifend), zeigt zusätzlich noch
// alle lokalen Drafts an, die noch nicht synchronisiert wurden.
export function MyMessages({ currentUser }) {
    const [threads, setThreads] = useState([]);
    const [active, setActive] = useState(null);
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState("");
    // Phase 4.10 (H3) — KI-Antwortvorschläge
    const [suggestions, setSuggestions] = useState([]);
    const [suggestionsLoading, setSuggestionsLoading] = useState(false);
    const [suggestionsError, setSuggestionsError] = useState("");
    // Feb-2026 Iter 28 (F) — KI-Vollantwort mit Tone-Auswahl
    const [fullReplyTone, setFullReplyTone] = useState("professional");
    const [fullReplyLoading, setFullReplyLoading] = useState(false);
    // Gast-Filter — Händler kann gezielt nur Gast-Anfragen anzeigen.
    // Deep-Link aus der „Gast-Leads"-Kachel (?guests=1) aktiviert den Filter direkt.
    const [msgParams] = useSearchParams();
    const [guestOnly, setGuestOnly] = useState(() => msgParams.get("guests") === "1");

    const loadThreads = useCallback(async () => {
        const { data } = await api.get("/messages/threads");
        setThreads(data);
    }, []);

    useEffect(() => { loadThreads(); }, [loadThreads]);

    const fetchSuggestions = useCallback(async (thread, msgs) => {
        if (!thread || !msgs || msgs.length === 0) return;
        // Nur sinnvoll, wenn LETZTE Nachricht vom Gegenüber stammt (sonst 400)
        if (msgs[msgs.length - 1].from_user_id === currentUser.id) {
            setSuggestions([]);
            setSuggestionsError("");
            return;
        }
        setSuggestionsLoading(true);
        setSuggestionsError("");
        try {
            const { data } = await api.post(`/messages/thread/${thread.thread_id}/ai-suggest`);
            setSuggestions(Array.isArray(data.suggestions) ? data.suggestions : []);
            if (data.mocked) setSuggestionsError("KI nicht erreichbar — keine Vorschläge.");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            setSuggestions([]);
            setSuggestionsError(typeof detail === "string" ? detail : "Vorschläge fehlgeschlagen.");
        } finally {
            setSuggestionsLoading(false);
        }
    }, [currentUser.id]);

    const openThread = async (t) => {
        setActive(t);
        setSuggestions([]);
        setSuggestionsError("");
        const { data } = await api.get(`/messages/thread/${t.thread_id}`);
        setMessages(data);
        fetchSuggestions(t, data);
    };

    // Jul-2026 (User-Wunsch) — Deep-Link ?thread=… öffnet den Vorgang direkt
    // (z. B. aus „Was zu tun ist" → einzelne ungelesene Nachricht).
    const [autoOpenedThread, setAutoOpenedThread] = useState(false);
    useEffect(() => {
        const tid = msgParams.get("thread");
        if (!tid || autoOpenedThread || threads.length === 0) return;
        const t = threads.find((x) => x.thread_id === tid);
        if (t) { setAutoOpenedThread(true); openThread(t); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [threads]);

    const send = async () => {
        if (!text.trim() || !active) return;
        await api.post("/messages", {
            listing_id: active.listing.id,
            to_user_id: active.other_user.id,
            content: text,
        });
        setText("");
        setSuggestions([]);
        const { data } = await api.get(`/messages/thread/${active.thread_id}`);
        setMessages(data);
        loadThreads();
    };

    const applySuggestion = (s, sendImmediately) => {
        if (sendImmediately) {
            setText(s);
            // schedule send after state commit
            setTimeout(() => {
                const btn = document.querySelector('[data-testid="reply-send"]');
                if (btn) btn.click();
            }, 50);
        } else {
            setText((cur) => cur ? `${cur} ${s}` : s);
        }
    };

    // Feb-2026 Iter 28 (F) — KI-Vollantwort generieren
    const generateFullReply = async () => {
        if (!active) return;
        // Letzte Nachricht des Gegenübers als Inquiry verwenden
        const buyerMsgs = messages.filter((m) => m.from_user_id !== currentUser.id);
        if (buyerMsgs.length === 0) {
            toast.info("Keine Käufer-Nachricht im Thread.");
            return;
        }
        const inquiry = buyerMsgs[buyerMsgs.length - 1].content || "";
        setFullReplyLoading(true);
        try {
            const { data } = await api.post("/listings/ai-smart-reply", {
                inquiry,
                listing: active.listing || {},
                dealer_name: currentUser.full_name || currentUser.dealer_name || currentUser.email || "Händler",
                tone: fullReplyTone,
            });
            if (data?.reply) {
                setText(data.reply);
                if (data.mocked) {
                    toast.info("KI-Vollantwort (Fallback) eingefügt — bitte vor dem Senden prüfen.");
                } else {
                    toast.success("KI-Vollantwort generiert — bitte vor dem Senden prüfen.");
                }
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Vollantwort konnte nicht generiert werden.");
        } finally {
            setFullReplyLoading(false);
        }
    };

    const guestCount = threads.filter((t) => t.other_user?.auth_provider === "guest").length;
    const visibleThreads = guestOnly
        ? threads.filter((t) => t.other_user?.auth_provider === "guest")
        : threads;

    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 min-h-[500px]">
            <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between gap-2">
                    <span className="font-display font-bold">Konversationen</span>
                    {guestCount > 0 && (
                        <button
                            type="button"
                            onClick={() => setGuestOnly((v) => !v)}
                            className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm border transition-colors ${guestOnly ? "bg-amber-500 text-white border-amber-500" : "bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100"}`}
                            data-testid="filter-guests-toggle"
                            title="Nur Anfragen von Gast-Konten anzeigen"
                        >
                            Nur Gäste ({guestCount})
                        </button>
                    )}
                </div>
                {visibleThreads.length === 0 ? (
                    <div className="p-6 text-center text-sm text-gray-500" data-testid="threads-empty">
                        {guestOnly ? "Keine Gast-Anfragen." : "Noch keine Nachrichten."}
                    </div>
                ) : (
                    <div className="divide-y divide-gray-100">
                        {visibleThreads.map((t) => (
                            <button
                                key={t.thread_id}
                                onClick={() => openThread(t)}
                                className={`w-full text-left p-4 hover:bg-gray-50 ${active?.thread_id === t.thread_id ? "bg-[#FFCC00]/20" : ""}`}
                                data-testid="thread-item"
                            >
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="font-bold text-sm truncate">{t.other_user?.company_name || t.other_user?.name}</span>
                                    {t.other_user?.auth_provider === "guest" && (
                                        <span className="bg-amber-100 text-amber-800 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm shrink-0" data-testid="thread-guest-badge">Gast</span>
                                    )}
                                    {t.unread > 0 && <span className="ml-auto bg-[#FFCC00] text-[#111827] text-[10px] font-bold px-1.5 rounded-sm">{t.unread}</span>}
                                </div>
                                <div className="text-xs text-gray-500 truncate">{t.listing?.title}</div>
                                <div className="text-xs text-gray-700 truncate mt-1">{t.last_message}</div>
                            </button>
                        ))}
                    </div>
                )}
            </div>

            <div className="md:col-span-2 bg-white border border-gray-200 rounded-sm flex flex-col">
                {!active ? (
                    <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
                        Wähle eine Konversation links aus
                    </div>
                ) : (
                    <>
                        <div className="px-5 py-4 border-b border-gray-200 flex items-center gap-3">
                            <div className="w-10 h-10 bg-[#FFCC00] flex items-center justify-center rounded-sm font-display font-bold">
                                {(active.other_user?.company_name || active.other_user?.name || "?").charAt(0)}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-bold truncate flex items-center gap-2">
                                    {active.other_user?.company_name || active.other_user?.name}
                                    {active.other_user?.auth_provider === "guest" && (
                                        <span className="bg-amber-100 text-amber-800 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm shrink-0" data-testid="active-thread-guest-badge">Gast</span>
                                    )}
                                </div>
                                <Link to={`/listing/${active.listing?.id}`} className="text-xs text-gray-500 hover:underline truncate block" target="_blank" rel="noopener noreferrer">{active.listing?.title}</Link>
                            </div>
                        </div>
                        <div className="flex-1 p-5 space-y-3 overflow-y-auto max-h-[400px]">
                            {messages.map((m) => {
                                const mine = m.from_user_id === currentUser.id;
                                return (
                                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                                        <div className={`max-w-[80%] px-4 py-2.5 rounded-sm text-sm ${mine ? "bg-[#111827] text-white" : "bg-gray-100 text-gray-900"}`}>
                                            <div>{m.content}</div>
                                            <div className={`text-[10px] mt-1 ${mine ? "text-white/60" : "text-gray-500"}`}>{formatDate(m.created_at)}</div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="p-4 border-t border-gray-200 flex flex-col gap-3">
                            {/* Phase 4.10 (H3) — KI-Antwortvorschläge */}
                            {(suggestions.length > 0 || suggestionsLoading || suggestionsError) && (
                                <div className="bg-amber-50 border border-amber-200 rounded-sm p-3" data-testid="ai-reply-suggestions">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Sparkles className="w-3.5 h-3.5 text-amber-700" />
                                        <span className="text-[11px] font-bold uppercase tracking-wider text-amber-900">KI-Antwortvorschläge</span>
                                        {suggestionsLoading && <Loader2 className="w-3 h-3 animate-spin text-amber-700" />}
                                        <button
                                            type="button"
                                            onClick={() => fetchSuggestions(active, messages)}
                                            disabled={suggestionsLoading}
                                            className="ml-auto text-[10px] font-bold uppercase tracking-wider text-amber-800 hover:text-amber-900 disabled:opacity-50"
                                            data-testid="ai-reply-refresh"
                                        >
                                            ↻ Neu generieren
                                        </button>
                                    </div>
                                    {suggestionsError && (
                                        <div className="text-[11px] text-gray-600 italic">{suggestionsError}</div>
                                    )}
                                    <div className="space-y-2">
                                        {suggestions.map((s, i) => (
                                            <div
                                                key={i}
                                                className="bg-white border border-amber-200 rounded-sm p-2.5 text-[12px] text-gray-800 flex items-start gap-2"
                                                data-testid={`ai-reply-suggestion-${i}`}
                                            >
                                                <div className="flex-1">{s}</div>
                                                <div className="flex flex-col gap-1 shrink-0">
                                                    <button
                                                        type="button"
                                                        onClick={() => applySuggestion(s, false)}
                                                        className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm bg-white border border-[#111827] text-[#111827] hover:bg-gray-100"
                                                        data-testid={`ai-reply-edit-${i}`}
                                                        title="Vorschlag in Textfeld übernehmen"
                                                    >
                                                        Bearbeiten
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => applySuggestion(s, true)}
                                                        className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm bg-[#111827] hover:bg-black text-white"
                                                        data-testid={`ai-reply-send-${i}`}
                                                        title="Vorschlag direkt senden"
                                                    >
                                                        Senden
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div className="flex gap-2 items-center flex-wrap" data-testid="full-reply-bar">
                                <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">KI-Vollantwort:</label>
                                <select
                                    value={fullReplyTone}
                                    onChange={(e) => setFullReplyTone(e.target.value)}
                                    className="text-[11px] border border-gray-300 rounded-sm px-2 py-1 bg-white"
                                    data-testid="full-reply-tone"
                                >
                                    <option value="professional">Professionell (Sie)</option>
                                    <option value="friendly">Freundlich (Du)</option>
                                    <option value="brief">Kurz & knapp</option>
                                </select>
                                <button
                                    type="button"
                                    onClick={generateFullReply}
                                    disabled={fullReplyLoading}
                                    className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-sm bg-[#111827] hover:bg-black text-[#FFCC00] disabled:opacity-50"
                                    data-testid="full-reply-generate"
                                >
                                    {fullReplyLoading
                                        ? <><Loader2 className="w-3 h-3 animate-spin" /> Generiere…</>
                                        : <><Sparkles className="w-3 h-3" /> Vollantwort generieren</>}
                                </button>
                                <span className="text-[10px] text-gray-400 italic">Antwort landet im Textfeld — vor dem Senden prüfen</span>
                            </div>
                            <div className="flex gap-2">
                                <Textarea
                                    value={text}
                                    onChange={(e) => setText(e.target.value)}
                                    placeholder="Antwort schreiben..."
                                    className="rounded-sm min-h-[60px]"
                                    data-testid="reply-textarea"
                                />
                                <Button onClick={send} className="bg-[#111827] hover:bg-black text-white rounded-sm self-end h-[60px]" data-testid="reply-send">
                                    <Send className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export function MyOffers({ currentUser }) {
    const { t } = useTranslation();
    const [received, setReceived] = useState([]);
    const [sent, setSent] = useState([]);
    const [view, setView] = useState("received");
    const [counterFor, setCounterFor] = useState(null);
    const [counterAmount, setCounterAmount] = useState("");
    const [counterMessage, setCounterMessage] = useState("");

    const load = useCallback(async () => {
        const [r, s] = await Promise.all([
            api.get("/offers/received"),
            api.get("/offers/sent"),
        ]);
        setReceived(r.data);
        setSent(s.data);
    }, []);

    useEffect(() => { load(); }, [load]);

    // Live: refresh offer lists when any offer involving this user changes
    useUserSocket(currentUser?.id, () => { load(); });

    const act = async (offerId, action, payload = null) => {
        try {
            await api.post(`/offers/${offerId}/${action}`, payload || {});
            toast.success({
                accept: "Vorschlag angenommen",
                reject: "Vorschlag abgelehnt",
                withdraw: "Vorschlag zurückgezogen",
                counter: "Gegenvorschlag gesendet",
            }[action] || "Aktion erfolgreich");
            await load();
            setCounterFor(null);
            setCounterAmount("");
            setCounterMessage("");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen");
        }
    };

    const list = view === "received" ? received : sent;

    return (
        <div>
            <div className="flex items-center gap-2 mb-4">
                <button
                    onClick={() => setView("received")}
                    className={`px-4 py-2 rounded-sm text-sm font-bold uppercase tracking-wider transition-colors ${view === "received" ? "bg-[#FFCC00] text-[#111827]" : "bg-white border border-gray-300 hover:border-[#111827]"}`}
                    data-testid="offers-tab-received"
                >
                    Erhalten ({received.length})
                </button>
                <button
                    onClick={() => setView("sent")}
                    className={`px-4 py-2 rounded-sm text-sm font-bold uppercase tracking-wider transition-colors ${view === "sent" ? "bg-[#FFCC00] text-[#111827]" : "bg-white border border-gray-300 hover:border-[#111827]"}`}
                    data-testid="offers-tab-sent"
                >
                    Gesendet ({sent.length})
                </button>
            </div>

            {list.length === 0 ? (
                <div className="bg-white border border-dashed border-gray-300 p-12 text-center rounded-sm">
                    <Handshake className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-600">{view === "received" ? "Noch keine erhaltenen Vorschläge." : "Noch keine gesendeten Vorschläge."}</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {list.map((o) => {
                        const other = view === "received" ? o.buyer : o.seller;
                        const otherName = other?.company_name || other?.name || "—";
                        const isCounter = o.status === "countered";
                        const isPending = o.status === "pending";
                        const isFinal = ["accepted", "rejected", "withdrawn"].includes(o.status);
                        const headlineAmount = o.final_amount || o.counter_amount || o.amount;
                        return (
                            <div key={o.id} className="bg-white border border-gray-200 rounded-sm p-5" data-testid="offer-item">
                                <div className="flex items-start gap-4">
                                    <Link to={`/listing/${o.listing?.id}`} className="w-20 h-20 bg-gray-100 rounded-sm overflow-hidden flex-shrink-0" target="_blank" rel="noopener noreferrer">
                                        {o.listing?.images?.[0] && <img src={o.listing.images[0]} alt="" className="w-full h-full object-cover" />}
                                    </Link>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <OfferStatusBadge status={o.status} />
                                            <span className="text-xs text-gray-500">{formatDate(o.updated_at)}</span>
                                        </div>
                                        <Link to={`/listing/${o.listing?.id}`} className="font-display font-bold hover:underline line-clamp-1" target="_blank" rel="noopener noreferrer">
                                            {o.listing?.title}
                                        </Link>
                                        <div className="text-xs text-gray-500 mt-0.5">
                                            {view === "received" ? "Von" : "An"}: <strong className="text-gray-700">{otherName}</strong>
                                            {" · "}Festpreis: {formatPrice(o.listing?.price)}
                                        </div>
                                        <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                                            <div>
                                                <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Vorschlag</div>
                                                <div className="font-mono-num font-bold">{formatPrice(o.amount)}</div>
                                            </div>
                                            {o.counter_amount && (
                                                <div>
                                                    <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Gegenvorschlag</div>
                                                    <div className="font-mono-num font-bold text-blue-700">{formatPrice(o.counter_amount)}</div>
                                                </div>
                                            )}
                                            {o.final_amount && (
                                                <div>
                                                    <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Zuschlag</div>
                                                    <div className="font-mono-num font-bold text-green-700">{formatPrice(o.final_amount)}</div>
                                                </div>
                                            )}
                                        </div>
                                        {o.message && (
                                            <p className="mt-2 text-xs text-gray-600 italic bg-gray-50 px-3 py-2 rounded-sm">„{o.message}“</p>
                                        )}
                                        {o.auto_rejected && (
                                            <div
                                                className="animate-slide-in-left mt-2 flex items-start gap-2 text-xs bg-red-50 border-l-4 border-red-500 px-3 py-2 rounded-sm overflow-hidden"
                                                data-testid={`offer-auto-rejected-${o.id}`}
                                            >
                                                <AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                                                <div className="min-w-0">
                                                    <div className="font-bold uppercase tracking-wider text-[10px] text-red-700 mb-0.5">
                                                        {t("offers.auto_rejected_label")}
                                                    </div>
                                                    <div className="text-red-900">
                                                        {formatAutoReject(o.auto_reject_code, o.auto_reject_params, o.auto_reject_reason)}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {o.counter_message && (
                                            <p className="mt-2 text-xs text-gray-600 italic bg-blue-50 px-3 py-2 rounded-sm">Gegen-Nachricht: „{o.counter_message}“</p>
                                        )}
                                        {view === "sent" && o.status === "countered" && o.auto_countered && (
                                            <div
                                                className="mt-2 flex items-start gap-2 text-xs bg-[#FFFBEB] border-l-4 border-[#FFCC00] px-3 py-2 rounded-sm"
                                                data-testid={`offer-auto-countered-${o.id}`}
                                            >
                                                <Zap className="w-4 h-4 text-[#B45309] shrink-0 mt-0.5" />
                                                <div className="min-w-0">
                                                    <div className="font-bold uppercase tracking-wider text-[10px] text-[#B45309] mb-0.5">
                                                        Automatischer Gegenvorschlag des Händlers
                                                    </div>
                                                    <div className="text-[#92400E] font-bold">
                                                        Der Händler nimmt {formatPrice(o.counter_amount)} sofort an — jetzt mit einem Klick zuschlagen.
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Actions */}
                                {view === "received" && isPending && (
                                    <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-100">
                                        <Button onClick={() => act(o.id, "accept")} className="bg-green-600 hover:bg-green-700 text-white font-bold rounded-sm" data-testid="seller-accept">
                                            <CheckCircle2 className="w-4 h-4 mr-1" /> Annehmen
                                        </Button>
                                        <Button onClick={() => setCounterFor(o.id)} variant="outline" className="border-blue-500 text-blue-700 hover:bg-blue-50 font-bold rounded-sm" data-testid="seller-counter">
                                            <TrendingDown className="w-4 h-4 mr-1" /> Gegenvorschlag
                                        </Button>
                                        <Button onClick={() => act(o.id, "reject")} variant="outline" className="border-red-200 text-red-700 hover:bg-red-50 font-bold rounded-sm" data-testid="seller-reject">
                                            <XCircle className="w-4 h-4 mr-1" /> Ablehnen
                                        </Button>
                                    </div>
                                )}
                                {view === "received" && counterFor === o.id && (
                                    <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-1 md:grid-cols-3 gap-2">
                                        <Input
                                            type="number"
                                            placeholder="Gegenvorschlag (€)"
                                            value={counterAmount}
                                            onChange={(e) => setCounterAmount(e.target.value)}
                                            className="rounded-sm font-mono-num font-bold"
                                            data-testid="counter-amount-input"
                                        />
                                        <Input
                                            placeholder="Nachricht (optional)"
                                            value={counterMessage}
                                            onChange={(e) => setCounterMessage(e.target.value)}
                                            className="rounded-sm md:col-span-2 md:col-start-1 md:col-end-3"
                                        />
                                        <Button
                                            onClick={() => act(o.id, "counter", { amount: Number(counterAmount), message: counterMessage || null })}
                                            className="bg-[#111827] hover:bg-black text-white font-bold rounded-sm"
                                            data-testid="counter-submit"
                                        >
                                            Senden <ArrowRight className="w-4 h-4 ml-1" />
                                        </Button>
                                    </div>
                                )}
                                {view === "sent" && isPending && (
                                    <div className="flex gap-2 mt-4 pt-4 border-t border-gray-100">
                                        <Button onClick={() => act(o.id, "withdraw")} variant="outline" className="rounded-sm border-gray-400 text-gray-700" data-testid="buyer-withdraw">
                                            Vorschlag zurückziehen
                                        </Button>
                                    </div>
                                )}
                                {view === "sent" && isCounter && (
                                    <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-100">
                                        <Button onClick={() => act(o.id, "accept")} className="bg-green-600 hover:bg-green-700 text-white font-bold rounded-sm" data-testid="buyer-accept-counter">
                                            <CheckCircle2 className="w-4 h-4 mr-1" /> {o.auto_countered ? "Jetzt annehmen" : "Gegenvorschlag annehmen"}
                                        </Button>
                                        <Button onClick={() => act(o.id, "reject")} variant="outline" className="border-red-200 text-red-700 hover:bg-red-50 font-bold rounded-sm" data-testid="buyer-reject-counter">
                                            <XCircle className="w-4 h-4 mr-1" /> Ablehnen
                                        </Button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export function MyTradeIns({ currentUser }) {
    const [received, setReceived] = useState([]);
    const [sent, setSent] = useState([]);
    const [counterFor, setCounterFor] = useState(null);
    const [counterAmount, setCounterAmount] = useState("");
    const [counterMessage, setCounterMessage] = useState("");

    const load = useCallback(async () => {
        const [r, s] = await Promise.all([
            api.get("/tradeins/received"),
            api.get("/tradeins/sent"),
        ]);
        setReceived(r.data);
        setSent(s.data);
    }, []);

    useEffect(() => { load(); }, [load]);
    useUserSocket(currentUser?.id, (evt) => {
        if (evt?.type?.startsWith?.("tradein_")) load();
    });

    const act = async (tradeinId, action, payload = null) => {
        try {
            await api.post(`/tradeins/${tradeinId}/${action}`, payload || {});
            toast.success({
                accept: "Anfrage angenommen",
                reject: "Anfrage abgelehnt",
                withdraw: "Anfrage zurückgezogen",
                counter: "Gegenvorschlag gesendet",
            }[action] || "Aktion erfolgreich");
            await load();
            setCounterFor(null);
            setCounterAmount("");
            setCounterMessage("");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen");
        }
    };

    const items = useMemo(() => [
        ...received.map((t) => ({ ...t, _kind: "received" })),
        ...sent.map((t) => ({ ...t, _kind: "sent" })),
    ], [received, sent]);

    const renderTradeIn = (t) => {
        const isSeller = t._kind === "received";
        const isCountering = counterFor === t.id;
        const finalPay = t.final_additional_payment ?? t.counter_additional_payment ?? t.additional_payment;
        const exp = deriveExpiry(t.created_at);
        const daysLeft = exp ? Math.ceil((exp - new Date()) / 86400000) : null;
        return (
            <div key={t.id} className="bg-white border border-gray-200 rounded-sm p-4" data-testid="tradein-card">
                <div className="flex items-start gap-4">
                    <Link to={`/listing/${t.listing_id}`} className="shrink-0" target="_blank" rel="noopener noreferrer">
                        {t.listing?.images?.[0] ? (
                            <img src={t.listing.images[0]} alt="" className="w-20 h-20 object-cover rounded-sm" />
                        ) : (
                            <div className="w-20 h-20 bg-gray-200 rounded-sm" />
                        )}
                    </Link>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 flex-wrap">
                            <div>
                                <Link to={`/listing/${t.listing_id}`} className="font-display font-bold text-lg hover:text-[#2563EB]" target="_blank" rel="noopener noreferrer">
                                    {t.listing?.title || "Inserat"}
                                </Link>
                                <div className="text-xs text-gray-500 mt-0.5">
                                    {isSeller ? "von" : "an"} <strong>{(isSeller ? t.buyer : t.seller)?.company_name || (isSeller ? t.buyer : t.seller)?.name || "—"}</strong>
                                    {" · "}{formatDate(t.updated_at)}
                                </div>
                                {exp && (
                                    <div className={`text-[11px] mt-0.5 font-mono-num ${daysLeft != null && daysLeft <= 3 ? "text-red-600 font-bold" : "text-gray-500"}`} data-testid={`tradein-expiry-${t.id}`}>
                                        Läuft ab: {exp.toLocaleDateString("de-DE")} {daysLeft != null && (daysLeft >= 0 ? `(in ${daysLeft} Tg.)` : "(abgelaufen)")}
                                    </div>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                <Badge className={`text-[10px] uppercase font-bold rounded-sm ${isSeller ? "bg-gray-100 text-gray-600 hover:bg-gray-100" : "bg-blue-100 text-blue-700 hover:bg-blue-100"}`}>
                                    {isSeller ? "Erhalten" : "Gesendet"}
                                </Badge>
                                <TradeInStatusBadge status={t.status} />
                            </div>
                        </div>

                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                            <div>
                                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Angebotenes Fahrzeug</div>
                                <div className="font-bold">{t.brand} {t.model}</div>
                                <div className="text-xs text-gray-500">
                                    {t.category === "motorcycle"
                                        ? `${t.mileage_km ? t.mileage_km.toLocaleString("de-DE") + " km" : ""}${t.tuev_until ? " · TÜV " + t.tuev_until : ""}${t.accident_free === true ? " · unfallfrei" : t.accident_free === false ? " · mit Schaden" : ""}`
                                        : `${t.bike_type || ""}${t.frame_size ? " · " + t.frame_size : ""}`}
                                </div>
                            </div>
                            <div>
                                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Zuzahlung</div>
                                <div className="font-display font-black text-xl font-mono-num">{formatPrice(finalPay)}</div>
                                {t.counter_additional_payment != null && t.status === "countered" && (
                                    <div className="text-xs text-gray-500">ursprünglich: <span className="font-mono-num">{formatPrice(t.additional_payment)}</span></div>
                                )}
                            </div>
                            <div>
                                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Reiner Verkauf</div>
                                <div className="font-mono-num">{t.sell_only_price ? formatPrice(t.sell_only_price) : "—"}</div>
                                {!!t.sell_only_price && (
                                    <p className="text-[10px] text-gray-500">Käufer würde alternativ nur verkaufen</p>
                                )}
                            </div>
                        </div>

                        {t.message && (
                            <p className="mt-3 bg-[#F8F9FA] border-l-2 border-[#2563EB] px-3 py-2 text-xs italic text-gray-700">&bdquo;{t.message}&ldquo;</p>
                        )}

                        {/* Image strip */}
                        {["img_papers", "img_front", "img_back", "img_left", "img_right", "img_dashboard", "img_damage"].some((k) => t[k]) && (
                            <div className="mt-3 flex gap-1 flex-wrap">
                                {["img_papers", "img_front", "img_back", "img_left", "img_right", "img_dashboard", "img_damage"].map((k) => t[k] && (
                                    <a key={k} href={t[k]} target="_blank" rel="noreferrer" className="w-14 h-14 rounded-sm overflow-hidden border border-gray-200 hover:border-[#2563EB]" title={k}>
                                        <img src={t[k]} alt={k} className="w-full h-full object-cover" />
                                    </a>
                                ))}
                            </div>
                        )}

                        {/* Actions */}
                        {isSeller && t.status === "pending" && !isCountering && (
                            <div className="mt-4 grid grid-cols-3 gap-2">
                                <Button onClick={() => act(t.id, "accept")} className="bg-green-600 hover:bg-green-700 text-white font-bold rounded-sm" data-testid="tradein-accept-btn">
                                    <CheckCircle2 className="w-4 h-4 mr-1" /> Annehmen
                                </Button>
                                <Button onClick={() => setCounterFor(t.id)} variant="outline" className="border-[#2563EB] text-[#2563EB] hover:bg-blue-50 font-bold rounded-sm" data-testid="tradein-counter-open-btn">
                                    <Repeat className="w-4 h-4 mr-1" /> Kontern
                                </Button>
                                <Button onClick={() => act(t.id, "reject")} variant="outline" className="border-red-200 text-red-700 hover:bg-red-50 font-bold rounded-sm" data-testid="tradein-reject-btn">
                                    <XCircle className="w-4 h-4 mr-1" /> Ablehnen
                                </Button>
                            </div>
                        )}
                        {isSeller && isCountering && (
                            <div className="mt-4 p-3 bg-blue-50 rounded-sm space-y-2">
                                <Input
                                    type="number"
                                    min="0"
                                    value={counterAmount}
                                    onChange={(e) => setCounterAmount(e.target.value)}
                                    placeholder="Gegenvorschlag Zuzahlung (€)"
                                    className="rounded-sm font-mono-num font-bold"
                                    data-testid="tradein-counter-input"
                                />
                                <Textarea value={counterMessage} onChange={(e) => setCounterMessage(e.target.value)} placeholder="Optionale Nachricht…" maxLength={500} className="rounded-sm min-h-[60px]" />
                                <div className="flex gap-2">
                                    <Button onClick={() => act(t.id, "counter", { additional_payment: Number(counterAmount), message: counterMessage.trim() || null })} disabled={!counterAmount} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-bold rounded-sm" data-testid="tradein-counter-send-btn">
                                        <Send className="w-4 h-4 mr-1" /> Senden
                                    </Button>
                                    <Button onClick={() => { setCounterFor(null); setCounterAmount(""); setCounterMessage(""); }} variant="outline" className="rounded-sm">Abbrechen</Button>
                                </div>
                            </div>
                        )}
                        {!isSeller && t.status === "pending" && (
                            <Button onClick={() => act(t.id, "withdraw")} variant="outline" className="mt-3 rounded-sm border-[#111827] text-sm" data-testid="tradein-withdraw-btn">
                                Anfrage zurückziehen
                            </Button>
                        )}
                        {!isSeller && t.status === "countered" && (
                            <div className="mt-3 grid grid-cols-2 gap-2">
                                <Button onClick={() => act(t.id, "accept")} className="bg-green-600 hover:bg-green-700 text-white font-bold rounded-sm">
                                    <CheckCircle2 className="w-4 h-4 mr-1" /> Gegenvorschlag annehmen
                                </Button>
                                <Button onClick={() => act(t.id, "reject")} variant="outline" className="border-red-200 text-red-700 hover:bg-red-50 font-bold rounded-sm">
                                    <XCircle className="w-4 h-4 mr-1" /> Ablehnen
                                </Button>
                            </div>
                        )}
                        {t.status === "accepted" && (
                            <div className="mt-3 flex items-center gap-3 text-sm">
                                <CheckCircle2 className="w-4 h-4 text-green-600" />
                                <span>Vereinbart: <strong className="font-mono-num">{formatPrice(finalPay)}</strong> Zuzahlung{t.paid ? " · bezahlt" : ""}</span>
                                <ArrowRight className="w-4 h-4 text-gray-400" />
                                <Link to={`/listing/${t.listing_id}`} className="text-[#2563EB] font-bold hover:underline" target="_blank" rel="noopener noreferrer">Zum Inserat</Link>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div data-testid="my-tradeins">
            <AnkaufList
                title="Inzahlungnahmen / Tausch"
                collapseKey="ankauf_tradeins"
                items={items}
                getKind={(t) => t._kind}
                getExpiry={(t) => deriveExpiry(t.created_at)}
                getOffer={(t) => (t.final_additional_payment ?? t.counter_additional_payment ?? t.additional_payment ?? 0)}
                getBrand={(t) => t.brand}
                emptyText="Keine Inzahlungnahmen für diese Auswahl."
                renderItem={renderTradeIn}
            />
        </div>
    );
}

export function DealerBuyRequests({ currentUser }) {
    const [data, setData] = useState({ items: [], hint: null });
    const [loading, setLoading] = useState(true);
    const [offerForId, setOfferForId] = useState(null);
    const [offerForm, setOfferForm] = useState({ amount: "", message: "", pickup_offered: false, valid_until: "" });
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/buy-requests");
            setData(data);
        } catch {
            // ignore
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const submitOffer = async (rid) => {
        const amt = Number(offerForm.amount);
        if (!Number.isFinite(amt) || amt <= 0) {
            toast.error("Bitte gültigen Betrag eingeben");
            return;
        }
        setBusy(true);
        try {
            const payload = {
                amount: amt,
                message: offerForm.message || undefined,
                pickup_offered: offerForm.pickup_offered || undefined,
                valid_until: offerForm.valid_until || undefined,
            };
            await api.post(`/buy-requests/${rid}/offers`, payload);
            toast.success("Angebot abgegeben — Verkäufer wurde benachrichtigt!");
            setOfferForId(null);
            setOfferForm({ amount: "", message: "", pickup_offered: false, valid_until: "" });
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Angebot fehlgeschlagen");
        } finally {
            setBusy(false);
        }
    };

    if (loading) return <div className="text-sm text-gray-500">Lädt…</div>;

    if (!currentUser.accept_buy_requests) {
        return (
            <div className="bg-amber-50 border border-amber-200 text-amber-900 p-6 rounded-sm" data-testid="buy-requests-disabled">
                <h3 className="font-bold mb-1 flex items-center gap-2"><ShoppingBag className="w-5 h-5" /> Ankäufe deaktiviert</h3>
                <p className="text-sm">Aktivieren Sie unter <strong>Einstellungen → Marktplatzeinstellungen → „An Geboten auf B2C-Verkaufsgesuche teilnehmen“</strong>, um Ankaufanfragen zu erhalten.</p>
            </div>
        );
    }

    if (data.items.length === 0) {
        return (
            <div className="bg-white border border-gray-200 rounded-sm p-10 text-center" data-testid="buy-requests-empty">
                <ShoppingBag className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <h3 className="font-bold">Keine Ankaufanfragen</h3>
                <p className="text-sm text-gray-500 mt-1">
                    Sobald Privatkunden Fahrzeuge passend zu Ihren {currentUser.favorite_brands?.length ? "Top-5 Lieblingsmarken" : "Marken"} anbieten, erscheinen die Anfragen hier.
                </p>
            </div>
        );
    }

    return (
        <AnkaufList
            title="Ankäufe (B2C / Privat)"
            collapseKey="ankauf_buy_requests"
            items={data.items}
            getKind={(it) => (it.my_offer ? "sent" : "received")}
            getExpiry={(it) => deriveExpiry(it.created_at)}
            getOffer={(it) => (it.my_offer?.amount ?? it.desired_price ?? 0)}
            getBrand={(it) => it.brand}
            emptyText="Keine Anfragen für diese Auswahl."
            renderItem={(it) => {
                const my = it.my_offer;
                const isOpen = it.status === "open";
                const exp = deriveExpiry(it.created_at);
                const daysLeft = exp ? Math.ceil((exp - new Date()) / 86400000) : null;
                return (
                    <div
                        key={it.id}
                        className="bg-white border border-gray-200 rounded-sm p-5 hover:border-[#111827] transition-colors cursor-pointer"
                        data-testid={`buy-request-${it.id}`}
                        onDoubleClick={() => {
                            window.open(`/buy-request/${it.id}`, "_blank", "noopener,noreferrer");
                        }}
                        title="Doppelklick öffnet die vollständigen Fahrzeugdaten in einem neuen Tab"
                    >
                        <div className="flex items-start justify-between gap-3 mb-3">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                    <Badge className="bg-[#111827] text-white hover:bg-[#111827] uppercase tracking-wide text-[10px] font-bold rounded-sm">{it.category === "motorcycle" ? "Motorrad" : "Fahrrad"}</Badge>
                                    <Badge className={`text-[10px] uppercase font-bold rounded-sm ${it.status === "accepted" ? "bg-green-100 text-green-700 hover:bg-green-100" : it.status === "cancelled" ? "bg-gray-100 text-gray-600 hover:bg-gray-100" : "bg-[#FFCC00] text-[#111827] hover:bg-[#FFCC00]"}`}>
                                        {it.status === "accepted" ? "Angenommen" : it.status === "cancelled" ? "Zurückgezogen" : "Offen"}
                                    </Badge>
                                    <Badge className={`text-[10px] uppercase font-bold rounded-sm ${my ? "bg-blue-100 text-blue-700 hover:bg-blue-100" : "bg-gray-100 text-gray-600 hover:bg-gray-100"}`}>
                                        {my ? "Gesendet" : "Erhalten"}
                                    </Badge>
                                </div>
                                <h3 className="font-display text-lg font-bold">{it.brand} {it.model}</h3>
                                <div className="text-xs text-gray-600 mt-0.5">
                                    {it.year} · PLZ {it.postal_code}{it.mileage_km ? ` · ${it.mileage_km.toLocaleString("de-DE")} km` : ""}{it.power_hp ? ` · ${it.power_hp} PS` : ""}{it.tuev_until ? ` · HU ${it.tuev_until}` : ""}
                                </div>
                                {it.desired_price && <div className="text-xs text-gray-700 mt-1">Wunschpreis: <strong>{Math.round(it.desired_price).toLocaleString("de-DE")} €</strong></div>}
                                {exp && (
                                    <div className={`text-[11px] mt-1 font-mono-num ${daysLeft != null && daysLeft <= 3 ? "text-red-600 font-bold" : "text-gray-500"}`} data-testid={`buy-request-expiry-${it.id}`}>
                                        Anfrage läuft ab: {exp.toLocaleDateString("de-DE")} {daysLeft != null && (daysLeft >= 0 ? `(in ${daysLeft} Tg.)` : "(abgelaufen)")}
                                    </div>
                                )}
                                {it.message && <p className="text-sm text-gray-600 mt-2 italic">&bdquo;{it.message}&ldquo;</p>}
                            </div>
                            {it.img_front && <img src={it.img_front} alt="" className="w-24 h-24 object-cover rounded-sm border border-gray-200 flex-shrink-0" />}
                        </div>

                        {my ? (
                            <div className={`mt-3 p-3 rounded-sm ${my.status === "accepted" ? "bg-green-50 border border-green-200" : my.status === "lost" ? "bg-gray-50 border border-gray-200" : "bg-[#FFFBEB] border border-[#FFCC00]"}`} data-testid={`my-offer-${it.id}`}>
                                <div className="text-xs font-bold uppercase tracking-wider text-gray-500">Ihr Angebot</div>
                                <div className="text-xl font-black font-mono-num mt-1">{Math.round(my.amount).toLocaleString("de-DE")} €</div>
                                <div className="text-xs text-gray-600 mt-1">Status: {my.status === "accepted" ? "Angenommen" : my.status === "lost" ? "Nicht ausgewählt" : "Offen"}</div>
                                {my.status === "accepted" && it.contact_email && (
                                    <div className="mt-2 pt-2 border-t border-green-300 text-xs">
                                        <strong>Kontakt:</strong> {it.contact_name} · {it.contact_email}{it.contact_phone ? ` · ${it.contact_phone}` : ""}
                                    </div>
                                )}
                            </div>
                        ) : isOpen ? (
                            offerForId === it.id ? (
                                <div className="mt-3 p-4 border border-gray-200 rounded-sm bg-gray-50 space-y-3" data-testid={`offer-form-${it.id}`}>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Angebot (€)</Label>
                                            <Input type="number" value={offerForm.amount} onChange={(e) => setOfferForm({ ...offerForm, amount: e.target.value })} placeholder="z. B. 9500" className="rounded-sm mt-1 font-mono-num" autoFocus data-testid={`offer-amount-${it.id}`} />
                                        </div>
                                        <div>
                                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Gültig bis</Label>
                                            <Input type="date" value={offerForm.valid_until} onChange={(e) => setOfferForm({ ...offerForm, valid_until: e.target.value })} className="rounded-sm mt-1 font-mono-num" />
                                        </div>
                                    </div>
                                    <div>
                                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Nachricht (optional)</Label>
                                        <Input value={offerForm.message} onChange={(e) => setOfferForm({ ...offerForm, message: e.target.value })} placeholder="z. B. abhängig von Probesitzung" className="rounded-sm mt-1" />
                                    </div>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input type="checkbox" checked={offerForm.pickup_offered} onChange={(e) => setOfferForm({ ...offerForm, pickup_offered: e.target.checked })} className="w-4 h-4 accent-[#FFCC00]" />
                                        <span className="text-sm">Abholung vor Ort angeboten</span>
                                    </label>
                                    <div className="flex gap-2">
                                        <Button onClick={() => submitOffer(it.id)} disabled={busy} className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold rounded-sm uppercase tracking-wider" data-testid={`offer-submit-${it.id}`}>
                                            {busy ? "Sendet…" : "Angebot abgeben"}
                                        </Button>
                                        <Button onClick={() => setOfferForId(null)} variant="outline" className="rounded-sm font-bold">Abbrechen</Button>
                                    </div>
                                </div>
                            ) : (
                                <Button onClick={() => setOfferForId(it.id)} className="bg-[#111827] hover:bg-black text-white font-bold rounded-sm uppercase tracking-wider" data-testid={`btn-make-offer-${it.id}`}>
                                    Angebot abgeben
                                </Button>
                            )
                        ) : (
                            <div className="text-xs text-gray-500 mt-2">Diese Anfrage akzeptiert keine weiteren Angebote.</div>
                        )}
                    </div>
                );
            }}
        />
    );
}

